import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Chartjs provides bundle-reducing options:
// https://www.chartjs.org/docs/latest/getting-started/integration.html#quick-start
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import { Button, ButtonGroup, Col, Row, Spinner, Table } from 'reactstrap';
import * as ajax from '../../../../actions/ajax';
import logger from '../../../../utils/logger';
import MetricCard from '../qualityAgentView/metricCard';
const DAY_OPTIONS = [7, 28];
const STUB_CARDS = [{
  title: 'Sync-blocked users',
  subtitle: 'Coming in Phase 2'
}, {
  title: 'Avg sync time',
  subtitle: 'Coming in Phase 2'
}, {
  title: 'Crashes / crash-free %',
  subtitle: 'Coming in Phase 2'
}, {
  title: 'RDS / WAF / scaling',
  subtitle: 'Coming in Phase 2'
}];
const TREND_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0
      }
    }
  }
};
const formatCustomers = customers => {
  if (!Array.isArray(customers) || customers.length === 0) {
    return '—';
  }
  if (customers.length <= 3) {
    return customers.join(', ');
  }
  return `${customers.slice(0, 3).join(', ')} +${customers.length - 3}`;
};
const formatSinceLabel = since => {
  if (!since) {
    return '';
  }
  const parsed = new Date(since);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return ` · since ${parsed.toLocaleString()}`;
};

/**
 * Health scorecard (Phase 1).
 * Live error metrics via GET /health/errors; other KPIs are Phase 2 stubs.
 */
const HealthDashboardView = ({
  label: _label = 'Health',
  days: initialDays = 7
}) => {
  const [days, setDays] = useState(DAY_OPTIONS.includes(initialDays) ? initialDays : 7);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const requestIdRef = useRef(0);
  const loadMetrics = useCallback(async windowDays => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await ajax.get('/health/errors', {
        days: windowDays
      });
      if (requestId !== requestIdRef.current) {
        return;
      }
      setMetrics(response.data);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      logger('Failed to load health error metrics', err === null || err === void 0 ? void 0 : err.message);
      setMetrics(null);
      setErrorMessage(ajax.getErrorMessage(err) || 'Failed to load error metrics.');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);
  useEffect(() => {
    loadMetrics(days);
  }, [days, loadMetrics]);
  const trendChart = useMemo(() => {
    const trend = Array.isArray(metrics === null || metrics === void 0 ? void 0 : metrics.trend) ? metrics.trend : [];
    return {
      labels: trend.map(point => point.day),
      datasets: [{
        label: 'Errors',
        data: trend.map(point => point.count),
        borderColor: '#f0ad4e',
        backgroundColor: 'rgba(240, 173, 78, 0.2)',
        tension: 0.2,
        fill: true
      }]
    };
  }, [metrics]);
  const hasTrendPoints = ((metrics === null || metrics === void 0 ? void 0 : metrics.trend) || []).length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: "t-health-dashboard p-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
    className: "mb-1"
  }, _label), /*#__PURE__*/React.createElement("div", {
    className: "text-muted small"
  }, "Live error cabinet \xB7 Phase 1", formatSinceLabel(metrics === null || metrics === void 0 ? void 0 : metrics.since))), /*#__PURE__*/React.createElement(ButtonGroup, {
    size: "sm"
  }, DAY_OPTIONS.map(option => /*#__PURE__*/React.createElement(Button, {
    key: option,
    color: days === option ? 'primary' : 'secondary',
    outline: days !== option,
    onClick: () => setDays(option)
  }, `${option}d`)))), errorMessage && /*#__PURE__*/React.createElement("div", {
    className: "alert alert-danger",
    role: "alert"
  }, errorMessage, /*#__PURE__*/React.createElement(Button, {
    color: "link",
    className: "p-0 ms-2 align-baseline",
    onClick: () => loadMetrics(days)
  }, "Retry")), loading && !metrics ? /*#__PURE__*/React.createElement("div", {
    className: "text-center py-5"
  }, /*#__PURE__*/React.createElement(Spinner, {
    color: "primary",
    "data-testid": "t-health-spinner"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Row, {
    className: "g-3 mb-4"
  }, /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: `Errors (${days}d)`,
    value: loading ? '…' : metrics === null || metrics === void 0 ? void 0 : metrics.errorCount,
    subtitle: "Live from error cabinet",
    color: "warning"
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Unique types",
    value: loading ? '…' : metrics === null || metrics === void 0 ? void 0 : metrics.uniqueTypes,
    subtitle: "Distinct error titles"
  })), STUB_CARDS.slice(0, 2).map(card => /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3,
    key: card.title
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: card.title,
    value: null,
    subtitle: card.subtitle
  })))), /*#__PURE__*/React.createElement(Row, {
    className: "g-3 mb-4"
  }, STUB_CARDS.slice(2).map(card => /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3,
    key: card.title
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: card.title,
    value: null,
    subtitle: card.subtitle
  })))), /*#__PURE__*/React.createElement("div", {
    className: "mb-2 fw-semibold"
  }, `Error trend (${days}d)`), /*#__PURE__*/React.createElement("div", {
    className: "bg-white border rounded p-3 mb-4",
    style: {
      height: 220
    },
    "data-testid": "t-health-trend"
  }, hasTrendPoints ? /*#__PURE__*/React.createElement(Line, {
    data: trendChart,
    options: TREND_OPTIONS
  }) : /*#__PURE__*/React.createElement("div", {
    className: "text-muted small h-100 d-flex align-items-center justify-content-center"
  }, loading ? 'Loading…' : 'No trend data for this window.')), /*#__PURE__*/React.createElement("div", {
    className: "mb-2 fw-semibold"
  }, "Top error types"), /*#__PURE__*/React.createElement(Table, {
    responsive: true,
    striped: true,
    size: "sm",
    className: "bg-white"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Title"), /*#__PURE__*/React.createElement("th", {
    className: "text-end"
  }, "Count"), /*#__PURE__*/React.createElement("th", null, "Customers"))), /*#__PURE__*/React.createElement("tbody", null, ((metrics === null || metrics === void 0 ? void 0 : metrics.topTypes) || []).length === 0 ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: 3,
    className: "text-muted"
  }, loading ? 'Loading…' : 'No errors in this window.')) : metrics.topTypes.map((row, index) =>
  /*#__PURE__*/
  // Titles can duplicate (e.g. multiple '(untitled)'); index disambiguates.
  // eslint-disable-next-line react/no-array-index-key
  React.createElement("tr", {
    key: `${row.title}-${index}`
  }, /*#__PURE__*/React.createElement("td", null, row.title), /*#__PURE__*/React.createElement("td", {
    className: "text-end"
  }, row.count), /*#__PURE__*/React.createElement("td", null, formatCustomers(row.customers)))))), /*#__PURE__*/React.createElement("p", {
    className: "text-muted small mb-0"
  }, "Phase 2 will keep this shell and add scheduled", ' ', /*#__PURE__*/React.createElement("code", null, "health_*"), ' ', "rollups (sync-blocked via sudoBehavior, excluding denyList). This view does not fleet-sudo on load.")));
};
HealthDashboardView.propTypes = {
  label: PropTypes.string,
  days: PropTypes.number
};
export default HealthDashboardView;